import React, { useState } from 'react';
import { Documento } from '@/types';

interface DocumentViewerProps {
  documento: Documento;
  onReportProblem?: (documentoId: string) => void;
}

export default function DocumentViewer({ documento, onReportProblem }: DocumentViewerProps) {
  const [viewMode, setViewMode] = useState<'info' | 'preview'>('info');

  const getFileIcon = (tipo?: string) => {
    if (!tipo) return <span className="material-symbols-outlined text-sm align-middle">description</span>;
    const tipoLower = tipo.toLowerCase();
    if (tipoLower.includes('pdf')) return <span className="material-symbols-outlined text-sm align-middle">description</span>;
    if (tipoLower.includes('image')) return <span className="material-symbols-outlined text-sm align-middle">image</span>;
    if (tipoLower.includes('word') || tipoLower.includes('document')) return <span className="material-symbols-outlined text-sm align-middle">edit_note</span>;
    if (tipoLower.includes('excel') || tipoLower.includes('spreadsheet')) return <span className="material-symbols-outlined text-sm align-middle">bar_chart</span>;
    if (tipoLower.includes('zip') || tipoLower.includes('rar')) return <span className="material-symbols-outlined text-sm align-middle">folder</span>;
    return <span className="material-symbols-outlined text-sm align-middle">attach_file</span>;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'contabilizado':
      case 'validado':
        return 'text-emerald-600 bg-emerald-50';
      case 'procesado_ia':
        return 'text-slate-600 bg-slate-100';
      case 'incidencia':
        return 'text-red-600 bg-red-50';
      case 'pendiente':
        return 'text-amber-600 bg-amber-50';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-calm-border bg-slate-50">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-calm-text flex items-center">
            <span className="mr-2">{getFileIcon(documento.tipo)}</span>
            {documento.nombre}
          </h3>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('info')}
              className={`px-3 py-1 text-xs rounded-full ${
                viewMode === 'info' 
                  ? 'bg-calm-text text-white' 
                  : 'bg-slate-100 text-calm-muted hover:bg-slate-200'
              }`}
            >
              Info
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-3 py-1 text-xs rounded-full ${
                viewMode === 'preview' 
                  ? 'bg-calm-text text-white' 
                  : 'bg-slate-100 text-calm-muted hover:bg-slate-200'
              }`}
            >
              Vista
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {viewMode === 'info' ? (
          <div className="p-4 space-y-4">
            {/* Basic Info */}
            <div className="bg-slate-50 rounded-2xl p-4">
              <h4 className="font-medium text-calm-text mb-3">Información del Documento</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-calm-muted">Tipo:</span>
                  <span className="font-medium text-calm-text capitalize">
                    {documento.tipo === 'emitida_pdf' || documento.tipo === 'emitida_excel' ? 'Factura Emitida' : 
                     documento.tipo === 'recibida_pdf' || documento.tipo === 'recibida_excel' ? 'Factura Recibida' :
                     'Otro'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-calm-muted">Estado:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(documento.estado || 'pendiente')}`}>
                    {(documento.estado || 'pendiente').replace('_', ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-calm-muted">Tamaño:</span>
                  <span className="font-medium text-calm-text">{formatFileSize(documento.tamaño || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-calm-muted">Subido:</span>
                  <span className="font-medium text-calm-text">
                    {new Date(documento.fechaSubida).toLocaleDateString('es-ES')}
                  </span>
                </div>
                {documento.nroAsientoERP && (
                  <div className="flex justify-between">
                    <span className="text-calm-muted">Asiento ERP:</span>
                    <span className="font-medium text-calm-text">{documento.nroAsientoERP}</span>
                  </div>
                )}
              </div>
            </div>

            {/* AI Data */}
            {documento.datosIA && (
              <div className="bg-slate-50 rounded-2xl p-4">
                <h4 className="font-medium text-calm-text mb-3 flex items-center">
                  <span className="mr-2"><span className="material-symbols-outlined text-sm align-middle">smart_toy</span></span>
                  Datos Procesados por IA
                </h4>
                <div className="space-y-2 text-sm">
                  {documento.datosIA.numeroFactura && (
                    <div className="flex justify-between">
                      <span className="text-calm-muted">Nº Factura:</span>
                      <span className="font-medium text-calm-text">{documento.datosIA.numeroFactura}</span>
                    </div>
                  )}
                  {documento.datosIA.fechaFactura && (
                    <div className="flex justify-between">
                      <span className="text-calm-muted">Fecha:</span>
                      <span className="font-medium text-calm-text">{documento.datosIA.fechaFactura}</span>
                    </div>
                  )}
                  {documento.datosIA.totalFactura !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-calm-muted">Total:</span>
                      <span className="font-light text-emerald-600">
                        {documento.datosIA.totalFactura.toFixed(2)}€
                      </span>
                    </div>
                  )}
                  {documento.datosIA.razonSocialEmisor && (
                    <div className="flex justify-between">
                      <span className="text-calm-muted">Emisor:</span>
                      <span className="font-medium text-calm-text">{documento.datosIA.razonSocialEmisor}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-calm-muted">Confianza IA:</span>
                    <span className={`font-medium ${
                      documento.datosIA.nivelConfianza > 0.8 ? 'text-emerald-600' :
                      documento.datosIA.nivelConfianza > 0.6 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {(documento.datosIA.nivelConfianza * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  if (documento.urlArchivo) {
                    window.open(documento.urlArchivo, '_blank');
                  } else {
                    alert('No hay archivo disponible para este documento.');
                  }
                }}
                disabled={!documento.urlArchivo}
                className={`btn btn-sm w-full ${documento.urlArchivo ? 'btn-secondary' : 'btn-disabled opacity-50 cursor-not-allowed'}`}
              >
                <span className="material-symbols-outlined text-sm align-middle">visibility</span> {documento.urlArchivo ? 'Ver Documento Completo' : 'Documento no disponible'}
              </button>
              {onReportProblem && (
                <button
                  onClick={() => onReportProblem(documento.id)}
                  className="btn btn-sm btn-warning w-full"
                >
                  <span className="material-symbols-outlined text-sm align-middle">warning</span> Reportar Problema
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full min-h-[500px] flex flex-col bg-slate-50">
            {documento.urlArchivo ? (
              (() => {
                const url = documento.urlArchivo;
                const nombre = documento.nombre?.toLowerCase() || '';
                const tipo = documento.tipo?.toLowerCase() || '';
                const isPdf = nombre.endsWith('.pdf') || tipo.includes('pdf');
                const isImage = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(nombre) || tipo.includes('image');

                if (isPdf) {
                  return (
                    <iframe
                      src={`${url}#toolbar=1&navpanes=0`}
                      className="w-full flex-1 border-0 min-h-[500px]"
                      title={documento.nombre}
                    />
                  );
                }

                if (isImage) {
                  return (
                    <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
                      <img
                        src={url}
                        alt={documento.nombre}
                        className="max-w-full max-h-full object-contain rounded shadow-subtle"
                      />
                    </div>
                  );
                }

                // Excel, Word, etc. — no inline preview
                return (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-6xl mb-4">{getFileIcon(documento.tipo)}</div>
                      <h3 className="text-lg font-medium text-calm-text mb-2">{documento.nombre}</h3>
                      <p className="text-calm-muted mb-4">Vista previa no disponible para este formato</p>
                      <button
                        onClick={() => window.open(url, '_blank')}
                        className="px-4 py-2 bg-calm-text text-white rounded-2xl hover:bg-slate-800 transition-colors"
                      >
                        Descargar / Abrir
                      </button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-6xl mb-4"><span className="material-symbols-outlined text-6xl">mail</span></div>
                  <p className="text-calm-muted">No hay archivo asociado a este documento</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
